import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Button,
  Input,
  Spinner,
  useTableFeatures,
  useTableSort,
  createTableColumn,
} from "@fluentui/react-components";
import { CiFilter } from "react-icons/ci";
import { DeleteRegular, Add20Filled, EditRegular, EyeRegular } from "@fluentui/react-icons";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "../CSS/ListEntitiesComponent.css";
import { deleteScope, getAllPermissions } from "../../Services/HeedifyServices";
import { getAccessToken } from "../../Services/GraphServices";
import { ConfirmAlert } from "../GenericComponent/ConfirmAlert";
import { useAbility } from "../../Contexts/AbilityContext";
import { useNotification } from '../GenericComponent/NotificationSystem';

function ListEntitiesComponent(props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const tenantId = props.ACTIVE_TENANT || activeAccount?.tenantId;
  
  const [entitiesRows, setEntitiesRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [deletingEntityName, setDeletingEntityName] = useState(null);
  const [filterVisible, setFilterVisible] = useState({ NAME: false });
  const [filters, setFilters] = useState({ NAME: "" });

  // 🔐 Ability pour permissions
  const ability = useAbility();
  const { showSuccess, showError } = useNotification();
  
  // Calculer si l'utilisateur peut créer dans au moins un scope
  // Si SCOPES_NAMES n'est pas défini ou vide, autoriser (super admin)
  const canCreateInAnyScope = useMemo(() => {
    if (!props.SCOPES_NAMES || props.SCOPES_NAMES.length === 0) return true;
    if (!ability) return false;
    return props.SCOPES_NAMES.some(scopeName => 
      ability.can("create", `Entity.main@${scopeName}`)
    );
  }, [ability, props.SCOPES_NAMES]);
  
  // Calculer les permissions pour tous les scopes avec useMemo
  const entitiesWithPermissions = useMemo(() => {
    if (!props.SCOPES_NAMES?.length || !ability) return [];
    
    return props.SCOPES_NAMES.map((name, index) => ({
      id: index,
      name: String(name),
      permissions: {
        main: {
          canCreate: ability.can("create", `Entity.main@${name}`),
         canRead: ability.can("read", `Entity.main@${name}`),
          canUpdate: ability.can("update", `Entity.main@${name}`),
          canDelete: ability.can("delete", `Entity.main@${name}`),
        }
      }
    }));
  }, [props.SCOPES_NAMES, ability]);

  // Initial load from Redux store and recalculate on SCOPES_NAMES change
  useEffect(() => {
    if (!props.SCOPES_NAMES?.length) {
      setEntitiesRows([]);
      setFilteredRows([]);
      return;
    }
    
    // Recalculate permissions directly to ensure fresh data
    const freshEntities = props.SCOPES_NAMES.map((name, index) => {
      // Si ability n'est pas encore disponible ou pas à jour, donner toutes les permissions
      // Cela évite que les boutons soient désactivés pour les entités nouvellement ajoutées
      const canCreate = ability ? ability.can("create", `Entity.main@${name}`) : true;
      const canRead = ability ? ability.can("read", `Entity.main@${name}`) : true;
      const canUpdate = ability ? ability.can("update", `Entity.main@${name}`) : true;
      const canDelete = ability ? ability.can("delete", `Entity.main@${name}`) : true;
      
      return {
        id: index,
        name: String(name),
        permissions: {
          main: {
            canCreate,
            canRead,
            canUpdate,
            canDelete,
          }
        }
      };
    });
    
    setEntitiesRows(freshEntities);
    setFilteredRows(freshEntities);
  }, [props.SCOPES_NAMES, ability]);

  // Apply filters function
  const applyFilters = (rows, filters) =>
    rows.filter((entity) =>
      entity.name.toLowerCase().includes(filters.NAME.toLowerCase())
    );

  // Update filtered rows when filters or entitiesRows change
  useEffect(() => {
    setFilteredRows(applyFilters(entitiesRows, filters));
  }, [filters, entitiesRows]);

  const columns = [
    createTableColumn({
      columnId: "NAME",
      compare: (a, b) => a.name?.localeCompare(b.name || "") ?? 0,
    }),
    createTableColumn({ columnId: "ACTION" }),
  ];

  const {
    getRows,
    sort: { getSortDirection, toggleColumnSort, sort },
  } = useTableFeatures({ columns, items: filteredRows }, [
    useTableSort({
      defaultSortState: { sortColumn: "NAME", sortDirection: "ascending" },
    }),
  ]);

  const rows = useMemo(() => {
    const sortedRows = sort(getRows());
    // Ne pas filtrer par canRead car ability peut ne pas être à jour immédiatement après ajout
    return sortedRows;
  }, [sort, getRows]);
  
  const headerSortProps = (columnId) => ({
    onClick: (e) => toggleColumnSort(e, columnId),
    sortDirection: getSortDirection(columnId),
  });

  const handleFilterChange = useCallback((columnKey, value) => {
    setFilters((prev) => ({ ...prev, [columnKey]: value }));
  }, []);

  const toggleFilterVisibility = useCallback((column) => {
    setFilterVisible((prev) => ({ ...prev, [column]: !prev[column] }));
  }, []);

  const confirmRemoveEntity = useCallback((entity) => setSelectedEntity(entity), []);

  // Delete function that updates Redux store using .then().catch()
  const removeEntity = (entityName) => {
    setDeletingEntityName(entityName);
    
    return getAccessToken(instance, activeAccount)
      .then((token) => {
        return deleteScope(tenantId, entityName, token);
      })
      .then((response) => {
        if (response?.data?.success && response?.status === 200) {
          // Refresh scopes from server
          return getAccessToken(instance, activeAccount)
            .then((token) => getAllPermissions(tenantId, token))
            .then((data) => {
              const { scopes, roles } = data;
              const scopeNames = Object.keys(scopes);
              props.setScopes(scopes);
              props.setRoles(roles);
              props.setScopesNames(scopeNames);
              showSuccess(t("ENTITY_DELETED_SUCCESS"));
            });
        } else {
          const errorMsg = response?.data?.message || "Erreur inconnue";
          console.error("Échec suppression entité:", response);
          showError(t("ENTITY_DELETE_FAILED"), errorMsg);
        }
        return Promise.resolve();
      })
      .catch((error) => {
        console.error("Error deleting entity:", error);
        showError(t("ENTITY_DELETE_ERROR"), error?.message);
      })
      .finally(() => {
        setDeletingEntityName(null);
        setSelectedEntity(null);
      });
  };

  const addEntity = useCallback(() => {
    navigate(`/entities/NewEntity`);
  }, [navigate]);

  const handleEdit = useCallback((entityName) => {
    localStorage.setItem('refreshScopesNeeded', 'true');
    navigate(`/entities/${entityName}`);
  }, [navigate]);

  // 🔐 Guards de permission
  if (!ability) {
    return <div className="access-denied-message">⏳ Loading...</div>;
  }

  return (
    <>
      <div >
        <div className="entity-list-header-container">
          <h2>{t("ENTITY_LIST")}</h2>
          <Button 
            appearance="primary" 
            onClick={addEntity} 
            className="entity-list-button-style"
            disabled={deletingEntityName !== null || !canCreateInAnyScope}
          >
            <Add20Filled /> {t("ADD_NEW_ENTITY")}
          </Button>
        </div>

        <div className="entity-list-main-container">
        <div className="entity-list-table-wrapper">
          <Table role="grid" aria-label="Entities table with filters and sorting" sortable>
            <TableHeader>
              <TableRow className="entity-list-header-style">
                <TableHeaderCell className="entity-list-cell-bold" {...headerSortProps("NAME")}>
                  <div className="entity-list-header-name-filter">
                    <div className="entity-list-name-with-icon">
                      <span>{t("NAME")}</span>
                      <CiFilter
                        className="filter-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFilterVisibility("NAME");
                        }}
                      />
                    </div>
                    {filterVisible.NAME && (
                      <Input
                        className="entity-list-filter-input-inline"
                        value={filters.NAME}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e, data) => handleFilterChange("NAME", data.value)}
                        placeholder={t("FILTER")}
                        size="small"
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell className="entity-list-cell-bold entity-list-action-header">{t("ACTION")}</TableHeaderCell>
              </TableRow>
            </TableHeader>

            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2}>
                    <TableCellLayout className="entity-list-empty-state">
                      {t("NO_ENTITIES_FOUND")}
                    </TableCellLayout>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map(({ item, rowId }) => {
                  return (
                    <TableRow key={rowId}>
                      <TableCell>
                        <TableCellLayout>{item.name}</TableCellLayout>
                      </TableCell>
                      <TableCell className="entity-list-action-cell">
                        <Button
                          appearance="subtle"
                          icon={item.permissions?.main?.canUpdate ? <EditRegular /> : <EyeRegular />}
                          aria-label={item.permissions?.main?.canUpdate ? "Edit" : "View"}
                          onClick={() => handleEdit(item.name)}
                          disabled={!item.permissions?.main?.canRead}
                        />
                        {/* ⚠️ LES DEUX `disabled` ÉTAIENT COMMENTÉS. Corbeille et crayon restaient
                            actifs pour TOUT LE MONDE, y compris un revendeur en lecture seule :
                            l'écran promettait une action que le serveur refusait ensuite. Les
                            permissions étaient pourtant calculées juste au-dessus — seul le
                            branchement manquait.
                            Ordre constant dans tout le portail : consulter à gauche, supprimer à
                            droite. Cet écran était le seul à faire l'inverse. */}
                        <Button
                          appearance="subtle"
                          icon={deletingEntityName === item.name ? <Spinner size="tiny" /> : <DeleteRegular />}
                          aria-label="Delete"
                          onClick={() => confirmRemoveEntity(item)}
                          disabled={deletingEntityName === item.name || !item.permissions?.main?.canDelete}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      </div>

      {selectedEntity && (
        <ConfirmAlert
          open={!!selectedEntity}
          name={selectedEntity.name}
          onConfirm={() => removeEntity(selectedEntity.name)}
          onCancel={() => setSelectedEntity(null)}
        />
      )}
    </>
  );
}

export default ListEntitiesComponent;